import React, { useState, useEffect } from 'react';
import { AiOutlineEdit, AiOutlineDelete, AiOutlinePlus, AiOutlineSave, AiOutlineClose, AiOutlineDatabase, AiOutlineFileText, AiOutlineUndo, AiOutlineRedo, AiOutlineStop, AiOutlineExport, AiOutlineImport } from 'react-icons/ai';
import { useDocumentTypeService, useRetentionPolicyService } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import { useConfirm } from '../components/ui/ConfirmProvider';
import { FaCheck, FaTimes, FaCog } from 'react-icons/fa';
import MetadataFieldsEditor from '../components/MetadataFieldsEditor';
import ImportSerieModal from '../components/ImportSerieModal';
import MaskEditor from '../components/MaskEditor';
import DocTreeSidebar, { buildTreeFromFlat, findPath as findTreePath } from '../components/DocTreeSidebar/DocTreeSidebar';
import FolderTreePicker from '../components/FolderTreePicker/FolderTreePicker';
import FolderCreateModal from '../components/FolderAdminModals/FolderCreateModal';
import FolderEditModal from '../components/FolderAdminModals/FolderEditModal';
import { GrDocument } from "react-icons/gr";
import {
  toBoolean,
  parseIntOrNull,
  parseFloatOrNull,
  normalizeSeries,
  normalizeFieldSpec,
  validateFieldIdentifier,
  validateIdentifier,
  IDENTIFIER_POLICY_HINT,
} from '../utils/seriesUtils';
import { ENV } from '../config/env';

const DocumentSeriesAdmin = ({ onBack }) => {
  const documentTypeService = useDocumentTypeService(); // Use new service hook
  const retentionPolicyService = useRetentionPolicyService();
  const notify = useNotification();
  const confirm = useConfirm();
  const [tiposDocumentos, setTiposDocumentos] = useState([]);
  const [tipoSeleccionado, setTipoSeleccionado] = useState(null);
  const [especificacion, setEspecificacion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editando, setEditando] = useState(false);
  const [campoEditando, setCampoEditando] = useState(null);
  const [nuevoCampo, setNuevoCampo] = useState({
    nombre: '',
    label: '',
    tipo: 'STRING',
    mask: '',
    required: false,
    uiFilterable: false,
    uiSortable: false,
    uiEditable: false,
    description: '',
    inputStrategies: []
  });
  const [historial, setHistorial] = useState([]);
  const [indiceHistorial, setIndiceHistorial] = useState(-1);
  const [valoresModificados, setValoresModificados] = useState(new Map());
  const [cambiosNoGuardados, setCambiosNoGuardados] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [hasDocuments, setHasDocuments] = useState(false);

  // New State for Series Management
  const [mode, setMode] = useState('view'); // 'view', 'create', 'edit-series'
  const [showProperties, setShowProperties] = useState(true); // Toggle for series properties section
  const [seriesFormData, setSeriesFormData] = useState({
    id: '',
    descripcion: '',
    padre: '', // Se selecciona del combo de nodos
    orden: 0,
    ocrRequired: false,
    reviewRequired: false,
    approvalRequired: false,
    signatureRequired: false,
    // Parametrización OCR (solo relevante si ocrRequired=true)
    ocrMaxFinePages: '',
    ocrQualityEnabled: false,
    ocrQualityMinConfidence: '',
    ocrQualityMinWordCount: '',
    ocrQualityMinTextCoverage: '',
    ocrQualityPageCountMin: '',
    ocrQualityPageCountMax: '',
    ocrQualityAutoReject: false,
    // Parametrización Firma Digital (#59, ADRs 023-028; solo relevante si signatureRequired=true)
    signatureIndividualAllowed: true,
    signatureBatchAllowed: false,
    preserveOriginalBinary: true,
    certMatchStrategy: '',
    revocationCheck: 'STRICT',
    padesLevel: 'B-T',
  });
  const [arbolNodes, setArbolNodes] = useState([]); // For parent dropdown (simulated or fetched)
  const [savingSeries, setSavingSeries] = useState(false);
  const [seriesDirty, setSeriesDirty] = useState(false);
  const [seriesIdError, setSeriesIdError] = useState(null);
  const [exportingSeries, setExportingSeries] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [serieCounts, setSerieCounts] = useState({}); // { serieId: totalCount }

  // Retention Policies
  const [policies, setPolicies] = useState([]);
  const [activePolicy, setActivePolicy] = useState('');
  const [initialActivePolicy, setInitialActivePolicy] = useState('');

  // Cargar series y nodos padre al montar el componente
  useEffect(() => {
    cargarTiposDocumentos();
    cargarPoliticas();

    // Cargar todos los folders del árbol (sin filtro ACL). Post ADR-022 el
    // endpoint devuelve folders de CUALQUIER profundidad — para el dropdown
    // de padre construimos el path completo abajo (ver getFolderPath).
    documentTypeService.getFolders()
      .then(response => {
        const folders = Array.isArray(response.data) ? response.data : [];
        setArbolNodes(folders);
      })
      .catch(err => console.warn('Error cargando nodos padre:', err));
  }, []);

  /**
   * Construye el path completo de un folder caminando hacia la raíz por
   * `parent`. Ej: para FICAL con cadena `FICAL → DEPAL → CNBA` devuelve
   * `"CNBA / DEPAL / FICAL"`. Imprescindible para distinguir folders
   * homónimos en distintas ramas del árbol (post ADR-022, el dropdown
   * lista TODOS los folders en lugar de solo nivel 1).
   */
  const getFolderPath = (folderId, nodes) => {
    const byId = new Map(nodes.map(n => [n.id, n]));
    const parts = [];
    let current = byId.get(folderId);
    let safety = 50; // anti-cycle (no debería ocurrir tras la validación del MOVE)
    while (current && safety-- > 0) {
      parts.unshift(current.name);
      current = current.parent ? byId.get(current.parent) : null;
    }
    return parts.join(' / ');
  };

  const cargarPoliticas = async () => {
    try {
      const resp = await retentionPolicyService.getPolicies();
      setPolicies(resp.data?.filter(p => p.active) || []);
    } catch (err) {
      console.error("Error loading policies", err);
    }
  };

  // Detectar cambios no guardados y alertar antes de salir
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (cambiosNoGuardados) {
        e.preventDefault();
        e.returnValue = 'Tienes cambios sin guardar. ¿Estás seguro de que quieres salir?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [cambiosNoGuardados]);

  const cargarTiposDocumentos = async () => {
    try {
      setLoading(true);
      // Pantalla de gestión: admin ve TODAS las series (scope=all). El endpoint
      // valida ADMIN/IMPORTER y devuelve 403 si no corresponde.
      const response = await documentTypeService.getTiposDocumentos({ scope: 'all' });
      const tipos = Array.isArray(response.data) ? response.data : (response.data?.items || []);
      const normalizados = tipos.map(normalizeSeries);
      setTiposDocumentos(normalizados);

      // Cargar total de documentos por serie en paralelo
      const counts = {};
      const promises = normalizados.map(async (tipo) => {
        try {
          const res = await documentTypeService.getSerieTotalCounts(tipo.id);
          const total = res.data?.totalDocCount ?? 0;
          return { id: tipo.id, total };
        } catch {
          return { id: tipo.id, total: 0 };
        }
      });
      const results = await Promise.all(promises);
      results.forEach(({ id, total }) => { counts[id] = total; });
      setSerieCounts(counts);
    } catch (err) {
      setError('Error cargando tipos de documentos: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const cargarEspecificacion = async (idTipoDoc) => {
    try {
      setLoading(true);
      setError(null);
      setLoading(true);
      setError(null);

      // If we are creating, we don't fetch spec
      if (idTipoDoc === 'new') {
        const newSeries = {
          id: '',
          descripcion: '',
          padre: '',
          orden: 0,
          ocrRequired: false,
          reviewRequired: false,
          approvalRequired: false,
          signatureRequired: false,
          ocrMaxFinePages: '',
          ocrQualityEnabled: false,
          ocrQualityMinConfidence: '',
          ocrQualityMinWordCount: '',
          ocrQualityMinTextCoverage: '',
          ocrQualityPageCountMin: '',
          ocrQualityPageCountMax: '',
          ocrQualityAutoReject: false,
          signatureIndividualAllowed: true,
          signatureBatchAllowed: false,
          preserveOriginalBinary: true,
          certMatchStrategy: '',
          revocationCheck: 'STRICT',
          padesLevel: 'B-T',
        };
        setSeriesFormData(newSeries);
        setEspecificacion({ fieldSpec: [] }); // Empty spec for new series
        setTipoSeleccionado(null); // Or some indicator that it's new
        setMode('create');
        setActivePolicy('');
        setInitialActivePolicy('');
        // Reset history for new
        setHistorial([{ especificacion: { fieldSpec: [] }, valoresModificados: new Map(), timestamp: Date.now() }]);
        setIndiceHistorial(0);
        setValoresModificados(new Map());
        setLoading(false);
        return;
      }

      setMode('edit-series'); // Default to allowing edit

      // Fetch document counts to check for restrictions
      try {
        const totalCountsRes = await documentTypeService.getSerieTotalCounts(idTipoDoc);
        // If there are documents (total > 0), we set hasDocuments to true
        const totalNonRejected = (totalCountsRes.data?.totalDocCount || 0);
        setHasDocuments(totalNonRejected > 0);
      } catch (countErr) {
        console.warn("Could not fetch document counts:", countErr);
        setHasDocuments(false);
      }

      // Fetch full series details from backend
      const fullTypeResponse = await documentTypeService.getTipoDocumento(idTipoDoc);
      const fullType = normalizeSeries(fullTypeResponse.data || {});

      try {
        const policyRes = await retentionPolicyService.getPoliciesByTipoDoc(idTipoDoc);
        const seriesPolicies = policyRes.data || [];
        if (seriesPolicies.length > 0) {
          setActivePolicy(seriesPolicies[0].idPolicy);
          setInitialActivePolicy(seriesPolicies[0].idPolicy);
        } else {
          setActivePolicy('');
          setInitialActivePolicy('');
        }
      } catch (err) {
        setActivePolicy('');
        setInitialActivePolicy('');
      }

      setSeriesFormData({
        id: fullType.id,
        descripcion: fullType.name || '',
        padre: fullType.parent || '',
        orden: fullType.order || 0,
        ocrRequired: fullType.ocrRequired,
        reviewRequired: fullType.reviewRequired,
        approvalRequired: fullType.approvalRequired,
        signatureRequired: fullType.signatureRequired,
        ocrMaxFinePages: fullType.ocrMaxFinePages ?? '',
        ocrQualityEnabled: !!fullType.ocrQualityEnabled,
        ocrQualityMinConfidence: fullType.ocrQualityMinConfidence ?? '',
        ocrQualityMinWordCount: fullType.ocrQualityMinWordCount ?? '',
        ocrQualityMinTextCoverage: fullType.ocrQualityMinTextCoverage ?? '',
        ocrQualityPageCountMin: fullType.ocrQualityPageCountMin ?? '',
        ocrQualityPageCountMax: fullType.ocrQualityPageCountMax ?? '',
        ocrQualityAutoReject: !!fullType.ocrQualityAutoReject,
        signatureIndividualAllowed: fullType.signatureIndividualAllowed ?? true,
        signatureBatchAllowed: !!fullType.signatureBatchAllowed,
        preserveOriginalBinary: fullType.preserveOriginalBinary ?? true,
        certMatchStrategy: fullType.certMatchStrategy ?? '',
        revocationCheck: fullType.revocationCheck ?? 'STRICT',
        padesLevel: fullType.padesLevel ?? 'B-T',
      });

      const response = await documentTypeService.getSpec(idTipoDoc);
      const normalizedSpec = {
        ...(response.data || {}),
        fieldSpec: (response.data?.fieldSpec || []).map(normalizeFieldSpec)
      };
      setEspecificacion(normalizedSpec);
      setTipoSeleccionado(idTipoDoc);

      // Guardar el estado inicial en el historial
      const nuevoHistorial = [{
        especificacion: JSON.parse(JSON.stringify(normalizedSpec)),
        valoresModificados: new Map(),
        timestamp: Date.now()
      }];
      setHistorial(nuevoHistorial);
      setIndiceHistorial(0);
      setValoresModificados(new Map());
    } catch (err) {
      setError('Error cargando especificación: ' + err.message);
      setEspecificacion(null);
    } finally {
      setLoading(false);
    }
  };

  const iniciarEdicionCampo = (campo) => {
    setCampoEditando(campo);
    setNuevoCampo({
      nombre: campo.name || campo.nombre,
      label: campo.label,
      tipo: campo.type || campo.tipo,
      mask: campo.mask || '',
      required: campo.required || false,
      uiFilterable: campo.uiFilterable || false,
      uiSortable: campo.uiSortable || false,
      uiEditable: campo.uiEditable || false,
      description: campo.description || ''
    });
    setEditando(true);
  };

  const cancelarEdicion = () => {
    setCampoEditando(null);
    setEditando(false);
    setNuevoCampo({ nombre: '', label: '', tipo: 'STRING', mask: '', required: false, uiFilterable: false, uiSortable: false, uiEditable: false, description: '', inputStrategies: [] });
  };

  const guardarCampo = () => {
    if (!campoEditando) return;

    const nombreNormalizado = (nuevoCampo.nombre || '').trim();
    const labelNormalizado = (nuevoCampo.label || '').trim();
    const validationError = validateFieldIdentifier(nombreNormalizado);
    if (validationError) {
      notify.error(validationError);
      return;
    }
    if (!labelNormalizado) {
      notify.error('La etiqueta del campo es obligatoria');
      return;
    }

    const isDuplicateName = especificacion.fieldSpec.some(
      campo => campo.nombre === nombreNormalizado && campo.nombre !== campoEditando.nombre
    );
    if (isDuplicateName) {
      notify.error(`Ya existe un campo con el nombre '${nombreNormalizado}'`);
      return;
    }

    const campoActualizado = {
      ...nuevoCampo,
      nombre: nombreNormalizado,
      label: labelNormalizado
    };

    // Usar nombre como identificador único en lugar de posición
    const camposActualizados = especificacion.fieldSpec.map(campo =>
      campo.nombre === campoEditando.nombre ? { ...campo, ...campoActualizado } : campo
    );

    const nuevaEspecificacion = {
      ...especificacion,
      fieldSpec: camposActualizados
    };

    // Marcar valores específicos como modificados usando el nombre del campo como identificador
    const nuevosValoresModificados = new Map(valoresModificados);
    const nombreCampo = campoEditando.nombre;

    // Comparar cada campo y marcar solo los que cambiaron
    if (campoEditando.nombre !== campoActualizado.nombre) {
      nuevosValoresModificados.set(`${nombreCampo}-nombre`, true);
    }
    if (campoEditando.label !== campoActualizado.label) {
      nuevosValoresModificados.set(`${nombreCampo}-label`, true);
    }
    if (campoEditando.tipo !== campoActualizado.tipo) {
      nuevosValoresModificados.set(`${nombreCampo}-tipo`, true);
    }

    // Comparar máscara considerando valores vacíos/undefined
    const maskOriginal = campoEditando.mask || '';
    const maskNueva = campoActualizado.mask || '';
    if (maskOriginal !== maskNueva) {
      nuevosValoresModificados.set(`${nombreCampo}-mask`, true);
    }

    const requiredOriginal = campoEditando.required || false;
    const requiredNuevo = campoActualizado.required || false;
    if (requiredOriginal !== requiredNuevo) {
      nuevosValoresModificados.set(`${nombreCampo}-required`, true);
    }

    // Comparar origin
    const originOriginal = campoEditando.origin || '';
    const originNueva = campoActualizado.origin || '';
    if (originOriginal !== originNueva) {
      nuevosValoresModificados.set(`${nombreCampo}-origin`, true);
    }

    // Comparar uiFilterable
    const uiFilterableOriginal = campoEditando.uiFilterable || false;
    const uiFilterableNueva = campoActualizado.uiFilterable || false;
    if (uiFilterableOriginal !== uiFilterableNueva) {
      nuevosValoresModificados.set(`${nombreCampo}-uiFilterable`, true);
    }

    // Comparar uiSortable
    const uiSortableOriginal = campoEditando.uiSortable || false;
    const uiSortableNueva = campoActualizado.uiSortable || false;
    if (uiSortableOriginal !== uiSortableNueva) {
      nuevosValoresModificados.set(`${nombreCampo}-uiSortable`, true);
    }

    // Comparar uiEditable
    const uiEditableOriginal = campoEditando.uiEditable || false;
    const uiEditableNueva = campoActualizado.uiEditable || false;
    if (uiEditableOriginal !== uiEditableNueva) {
      nuevosValoresModificados.set(`${nombreCampo}-uiEditable`, true);
    }

    // Comparar description
    const descriptionOriginal = campoEditando.description || '';
    const descriptionNueva = campoActualizado.description || '';
    if (descriptionOriginal !== descriptionNueva) {
      nuevosValoresModificados.set(`${nombreCampo}-description`, true);
    }

    // Guardar en historial con el nuevo estado
    guardarEnHistorial(nuevaEspecificacion, nuevosValoresModificados);

    setEspecificacion(nuevaEspecificacion);
    setValoresModificados(nuevosValoresModificados);
    setCambiosNoGuardados(true);

    cancelarEdicion();
  };

  const eliminarCampo = (nombreCampo) => {
    const camposActualizados = especificacion.fieldSpec
      .filter(campo => campo.nombre !== nombreCampo)
      .map((campo, index) => ({ ...campo, posicion: index + 1 }));

    const nuevaEspecificacion = {
      ...especificacion,
      fieldSpec: camposActualizados
    };

    // Limpiar valores modificados ya que se reordenaron
    const nuevosValoresModificados = new Map();

    // Guardar en historial con el nuevo estado
    guardarEnHistorial(nuevaEspecificacion, nuevosValoresModificados);

    setEspecificacion(nuevaEspecificacion);
    setValoresModificados(nuevosValoresModificados);
    setCambiosNoGuardados(true);
  };

  const moverCampo = (nombreCampo, direccion) => {
    const campos = [...especificacion.fieldSpec];
    // Buscar el campo por nombre en lugar de posición
    const indiceActual = campos.findIndex(campo => campo.nombre === nombreCampo);
    if (indiceActual === -1) return; // Campo no encontrado

    const nuevoIndice = direccion === 'up' ? indiceActual - 1 : indiceActual + 1;

    if (nuevoIndice >= 0 && nuevoIndice < campos.length) {
      // Guardar estado actual ANTES del cambio
      const nuevoHistorial = historial.slice(0, indiceHistorial + 1);
      nuevoHistorial.push({
        especificacion: JSON.parse(JSON.stringify(especificacion)),
        valoresModificados: new Map(valoresModificados),
        timestamp: Date.now()
      });

      setHistorial(nuevoHistorial);
      setIndiceHistorial(nuevoHistorial.length - 1);

      // Intercambiar elementos en el array
      const campoActual = campos[indiceActual];
      const campoIntercambio = campos[nuevoIndice];

      // Intercambiar en el array
      campos[indiceActual] = campoIntercambio;
      campos[nuevoIndice] = campoActual;

      // Actualizar posiciones basadas en el nuevo orden
      campos.forEach((campo, index) => {
        campo.posicion = index + 1;
      });

      const nuevaEspecificacion = {
        ...especificacion,
        fieldSpec: campos
      };

      // Marcar posiciones como modificadas usando los nombres de los campos
      const nuevosValoresModificados = new Map(valoresModificados);
      nuevosValoresModificados.set(`${campoActual.nombre}-posicion`, true);
      nuevosValoresModificados.set(`${campoIntercambio.nombre}-posicion`, true);

      setEspecificacion(nuevaEspecificacion);
      setValoresModificados(nuevosValoresModificados);
      setCambiosNoGuardados(true);
    }
  };

  const guardarEnHistorial = (nuevaEspecificacion, nuevosValoresModificados) => {
    const nuevoHistorial = historial.slice(0, indiceHistorial + 1);
    nuevoHistorial.push({
      especificacion: JSON.parse(JSON.stringify(nuevaEspecificacion)),
      valoresModificados: new Map(nuevosValoresModificados),
      timestamp: Date.now()
    });

    setHistorial(nuevoHistorial);
    setIndiceHistorial(nuevoHistorial.length - 1);
  };

  const undo = () => {
    if (indiceHistorial > 0) {
      const nuevoIndice = indiceHistorial - 1;
      const estadoAnterior = historial[nuevoIndice];

      setEspecificacion(estadoAnterior.especificacion);
      setValoresModificados(estadoAnterior.valoresModificados);
      setIndiceHistorial(nuevoIndice);
    }
  };

  const redo = () => {
    if (indiceHistorial < historial.length - 1) {
      const nuevoIndice = indiceHistorial + 1;
      const estadoSiguiente = historial[nuevoIndice];

      setEspecificacion(estadoSiguiente.especificacion);
      setValoresModificados(estadoSiguiente.valoresModificados);
      setIndiceHistorial(nuevoIndice);
    }
  };

  const reset = async () => {
    const ok = await confirm({
      title: 'Descartar cambios',
      message: '¿Estás seguro de descartar los cambios y volver al estado original?',
      confirmLabel: 'Descartar',
      destructive: true,
    });
    if (ok && tipoSeleccionado) {
      cargarEspecificacion(tipoSeleccionado);
    }
  };

  const guardarCambios = async () => {
    if (!especificacion || !tipoSeleccionado) return;

    try {
      setGuardando(true);

      // Validar unicidad de posiciones CSV
      const csvFields = (especificacion.fieldSpec || []).filter(f =>
        (f.inputStrategies || []).some(s => s.strategyType === 'CSV_POSITIONAL')
      );
      const csvPositions = csvFields.map(f => {
        const s = f.inputStrategies.find(s => s.strategyType === 'CSV_POSITIONAL');
        return s?.config?.position;
      }).filter(p => p != null);
      if (new Set(csvPositions).size !== csvPositions.length) {
        notify.error('Hay campos CSV con la misma posición. Corregí las posiciones antes de guardar.');
        setGuardando(false);
        return;
      }

      // Llamar al backend para guardar los cambios
      await documentTypeService.updateSpec(tipoSeleccionado, especificacion);

      // Reload the specification from the server to reflect any server-side changes
      const response = await documentTypeService.getSpec(tipoSeleccionado);
      const normalizedSpec = {
        ...(response.data || {}),
        fieldSpec: (response.data?.fieldSpec || []).map(normalizeFieldSpec)
      };
      setEspecificacion(normalizedSpec);

      // Reset history with the freshly loaded spec
      setHistorial([{
        especificacion: JSON.parse(JSON.stringify(normalizedSpec)),
        valoresModificados: new Map(),
        timestamp: Date.now()
      }]);
      setIndiceHistorial(0);

      // Marcar como guardado
      setCambiosNoGuardados(false);
      setValoresModificados(new Map());

      notify.success('Cambios guardados exitosamente');
    } catch (err) {
      console.error('Error guardando cambios:', err);
      
      // Manejar errores de validación del backend
      if (err.response?.status === 400 && err.response?.data?.validationErrors) {
        const errors = err.response.data.validationErrors;
        errors.forEach(e => {
          notify.error(`${e.message}`);
        });
      } else {
        notify.error('Error al guardar los cambios: ' + (err.response?.data?.message || err.message));
      }
    } finally {
      setGuardando(false);
    }
  };

  const agregarCampo = () => {
    const nombreNormalizado = (nuevoCampo.nombre || '').trim();
    const labelNormalizado = (nuevoCampo.label || '').trim();
    const validationError = validateFieldIdentifier(nombreNormalizado);
    if (validationError) {
      notify.error(validationError);
      return;
    }
    if (!labelNormalizado) {
      notify.error('La etiqueta del campo es obligatoria');
      return;
    }
    if (especificacion.fieldSpec.some(campo => campo.nombre === nombreNormalizado)) {
      notify.error(`Ya existe un campo con el nombre '${nombreNormalizado}'`);
      return;
    }

    const nuevoCampoNormalizado = {
      ...nuevoCampo,
      nombre: nombreNormalizado,
      label: labelNormalizado
    };

    const nuevoPosicion = especificacion.fieldSpec.length + 1;
    const nuevoCampoCompleto = normalizeFieldSpec({
      ...nuevoCampoNormalizado,
      posicion: nuevoPosicion,
      displayOrder: nuevoPosicion,
    });

    const nuevaEspecificacion = {
      ...especificacion,
      fieldSpec: [...especificacion.fieldSpec, nuevoCampoCompleto]
    };

    // Marcar todos los valores del nuevo campo como modificados usando el nombre del campo
    const nuevosValoresModificados = new Map(valoresModificados);
    nuevosValoresModificados.set(`${nuevoCampoNormalizado.nombre}-nombre`, true);
    nuevosValoresModificados.set(`${nuevoCampoNormalizado.nombre}-label`, true);
    nuevosValoresModificados.set(`${nuevoCampoNormalizado.nombre}-tipo`, true);
    nuevosValoresModificados.set(`${nuevoCampoNormalizado.nombre}-mask`, true);
    nuevosValoresModificados.set(`${nuevoCampoNormalizado.nombre}-required`, true);
    if (nuevoCampoNormalizado.origin) {
      nuevosValoresModificados.set(`${nuevoCampoNormalizado.nombre}-origin`, true);
    }
    if (nuevoCampoNormalizado.uiFilterable) {
      nuevosValoresModificados.set(`${nuevoCampoNormalizado.nombre}-uiFilterable`, true);
    }
    if (nuevoCampoNormalizado.uiSortable) {
      nuevosValoresModificados.set(`${nuevoCampoNormalizado.nombre}-uiSortable`, true);
    }
    if (nuevoCampoNormalizado.uiEditable) {
      nuevosValoresModificados.set(`${nuevoCampoNormalizado.nombre}-uiEditable`, true);
    }
    if (nuevoCampoNormalizado.description) {
      nuevosValoresModificados.set(`${nuevoCampoNormalizado.nombre}-description`, true);
    }

    // Guardar en historial con el nuevo estado
    guardarEnHistorial(nuevaEspecificacion, nuevosValoresModificados);

    setEspecificacion(nuevaEspecificacion);
    setValoresModificados(nuevosValoresModificados);
    setCambiosNoGuardados(true);
    setNuevoCampo({ nombre: '', label: '', tipo: 'STRING', mask: '', required: false, uiFilterable: false, uiSortable: false, uiEditable: false, description: '', inputStrategies: [] });
  };

  const handleCreateNew = () => {
    cargarEspecificacion('new');
  };

  /**
   * Exporta la definición de la serie seleccionada como archivo JSON descargable (FSD-005).
   * El backend arma el JSON con árbol + fields + strategies y responde con
   * Content-Disposition; acá extraemos el filename y gatillamos la descarga.
   */
  const handleExportSerie = async () => {
    if (!seriesFormData.id) return;
    setExportingSeries(true);
    try {
      const response = await documentTypeService.exportTipoDocumento(seriesFormData.id);

      // Extraer filename del Content-Disposition; fallback seguro con timestamp UTC.
      const dispositionHeader = response.headers?.['content-disposition']
        ?? response.headers?.get?.('content-disposition');
      const match = dispositionHeader && /filename="?([^";]+)"?/i.exec(dispositionHeader);
      // Fallback: yyyyMMdd_HHmm en UTC (formato determinista consistente con el backend).
      const iso = new Date().toISOString(); // YYYY-MM-DDTHH:mm:ss.sssZ
      const fallbackTs = `${iso.slice(0, 10).replace(/-/g, '')}_${iso.slice(11, 16).replace(':', '')}`;
      const filename = match ? match[1] : `${seriesFormData.id}-${fallbackTs}.json`;

      // Gatillar la descarga con un anchor temporal.
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      notify.success(`Serie "${seriesFormData.descripcion}" exportada`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'error desconocido';
      notify.error(`No se pudo exportar la serie: ${msg}`);
    } finally {
      setExportingSeries(false);
    }
  };

  const handleSaveSeries = async () => {
    const errors = [];
    if (mode === 'create' && !seriesFormData.id?.trim()) errors.push('ID de la serie');
    if (!seriesFormData.descripcion?.trim()) errors.push('Descripción');
    if (!seriesFormData.padre?.trim()) errors.push('Nodo Padre');
    if (errors.length > 0) {
      notify.error(`Campos obligatorios faltantes: ${errors.join(', ')}`);
      return;
    }

    // Validación de formato del ID en alta — replica la regla del backend para
    // evitar el roundtrip 400. El usuario ya tiene el feedback inline al perder
    // foco; este chequeo evita que pueda forzar el submit con un ID inválido.
    if (mode === 'create') {
      const idErr = validateIdentifier(seriesFormData.id, 'ID de la serie');
      if (idErr) {
        setSeriesIdError(idErr);
        notify.error(idErr);
        return;
      }
    }

    setSavingSeries(true);
    try {
      // Transform to API format (camelCase)
      const apiPayload = {
        id: seriesFormData.id,
        name: seriesFormData.descripcion,
        parent: seriesFormData.padre,
        order: seriesFormData.orden,
        ocrRequired: toBoolean(seriesFormData.ocrRequired),
        reviewRequired: toBoolean(seriesFormData.reviewRequired),
        approvalRequired: toBoolean(seriesFormData.approvalRequired),
        signatureRequired: toBoolean(seriesFormData.signatureRequired),
        // Parametrización OCR: solo enviar si OCR está activo (si no, API conserva nulls)
        ocrMaxFinePages: toBoolean(seriesFormData.ocrRequired) ? parseIntOrNull(seriesFormData.ocrMaxFinePages) : null,
        ocrQualityEnabled: toBoolean(seriesFormData.ocrRequired) ? toBoolean(seriesFormData.ocrQualityEnabled) : false,
        ocrQualityMinConfidence: toBoolean(seriesFormData.ocrRequired) ? parseFloatOrNull(seriesFormData.ocrQualityMinConfidence) : null,
        ocrQualityMinWordCount: toBoolean(seriesFormData.ocrRequired) ? parseIntOrNull(seriesFormData.ocrQualityMinWordCount) : null,
        ocrQualityMinTextCoverage: toBoolean(seriesFormData.ocrRequired) ? parseFloatOrNull(seriesFormData.ocrQualityMinTextCoverage) : null,
        ocrQualityPageCountMin: toBoolean(seriesFormData.ocrRequired) ? parseIntOrNull(seriesFormData.ocrQualityPageCountMin) : null,
        ocrQualityPageCountMax: toBoolean(seriesFormData.ocrRequired) ? parseIntOrNull(seriesFormData.ocrQualityPageCountMax) : null,
        ocrQualityAutoReject: toBoolean(seriesFormData.ocrRequired) ? toBoolean(seriesFormData.ocrQualityAutoReject) : false,
        // Parametrización Firma Digital (#59): solo enviar valores efectivos si signatureRequired=true.
        signatureIndividualAllowed: toBoolean(seriesFormData.signatureRequired) ? toBoolean(seriesFormData.signatureIndividualAllowed) : true,
        signatureBatchAllowed: toBoolean(seriesFormData.signatureRequired) ? toBoolean(seriesFormData.signatureBatchAllowed) : false,
        preserveOriginalBinary: toBoolean(seriesFormData.signatureRequired) ? toBoolean(seriesFormData.preserveOriginalBinary) : true,
        certMatchStrategy: toBoolean(seriesFormData.signatureRequired) ? (seriesFormData.certMatchStrategy || null) : null,
        revocationCheck: toBoolean(seriesFormData.signatureRequired) ? (seriesFormData.revocationCheck || 'STRICT') : 'STRICT',
        padesLevel: toBoolean(seriesFormData.signatureRequired) ? (seriesFormData.padesLevel || 'B-T') : 'B-T',
        isSeries: true,
        state: 1
      };

      if (mode === 'create') {
        await documentTypeService.createTipoDocumento(apiPayload);
        notify.success("Serie documental creada exitosamente");
        if (activePolicy) {
          try {
             await retentionPolicyService.associatePolicy(activePolicy, seriesFormData.id);
          } catch (e) {
             notify.error("Se creó la serie pero hubo un error asociando la política.");
          }
        }
        await cargarTiposDocumentos();
        setMode('edit-series');
        setTipoSeleccionado(seriesFormData.id);
        setSeriesDirty(false);
      } else {
        await documentTypeService.updateTipoDocumento(seriesFormData.id, apiPayload);
        
        // Update policy association
        if (activePolicy !== initialActivePolicy) {
           try {
              if (initialActivePolicy) {
                 await retentionPolicyService.dissociatePolicy(initialActivePolicy, seriesFormData.id);
              }
              if (activePolicy) {
                 await retentionPolicyService.associatePolicy(activePolicy, seriesFormData.id);
              }
           } catch (e) {
              notify.error("Error al actualizar la asociación de la política.");
           }
        }

        notify.success("Serie documental actualizada exitosamente");
        await cargarTiposDocumentos();
        setInitialActivePolicy(activePolicy);
      }
    } catch (err) {
      console.error("Error saving series:", err);
      // ProblemDetails del backend trae { detail, errors: [{field, message}], ... }
      // axios lo expone en err.response.data. Preferimos cada error específico antes
      // que el "Request failed with status code 400" genérico de axios.
      const data = err.response?.data;
      if (Array.isArray(data?.errors) && data.errors.length > 0) {
        data.errors.forEach((e) => {
          notify.error(e.message || `Error en campo ${e.field || ''}`);
          if (e.field === 'id' && mode === 'create') {
            setSeriesIdError(e.message || 'ID inválido');
          }
        });
      } else {
        notify.error('Error al guardar serie: ' + (data?.detail || data?.message || err.message));
      }
    } finally {
      setSavingSeries(false);
    }
  };

  const updateSeriesForm = (data) => { setSeriesFormData(data); setSeriesDirty(true); };

  const guardarTodo = async () => {
    // Validar propiedades de la serie
    const errors = [];
    if (!seriesFormData.descripcion?.trim()) errors.push('Descripción');
    if (!seriesFormData.padre?.trim()) errors.push('Nodo Padre');
    if (errors.length > 0) {
      notify.error(`Campos obligatorios faltantes: ${errors.join(', ')}`);
      return;
    }

    // Validar unicidad de posiciones CSV
    const csvFields = (especificacion?.fieldSpec || []).filter(f =>
      (f.inputStrategies || []).some(s => s.strategyType === 'CSV_POSITIONAL')
    );
    const csvPositions = csvFields.map(f => {
      const s = f.inputStrategies.find(s => s.strategyType === 'CSV_POSITIONAL');
      return s?.config?.position;
    }).filter(p => p != null);
    if (new Set(csvPositions).size !== csvPositions.length) {
      notify.error('Hay campos CSV con la misma posición. Corregí las posiciones antes de guardar.');
      return;
    }

    setGuardando(true);
    try {
      const apiPayload = {
        id: seriesFormData.id,
        name: seriesFormData.descripcion,
        parent: seriesFormData.padre,
        order: seriesFormData.orden,
        ocrRequired: toBoolean(seriesFormData.ocrRequired),
        reviewRequired: toBoolean(seriesFormData.reviewRequired),
        approvalRequired: toBoolean(seriesFormData.approvalRequired),
        signatureRequired: toBoolean(seriesFormData.signatureRequired),
        ocrMaxFinePages: toBoolean(seriesFormData.ocrRequired) ? parseIntOrNull(seriesFormData.ocrMaxFinePages) : null,
        ocrQualityEnabled: toBoolean(seriesFormData.ocrRequired) ? toBoolean(seriesFormData.ocrQualityEnabled) : false,
        ocrQualityMinConfidence: toBoolean(seriesFormData.ocrRequired) ? parseFloatOrNull(seriesFormData.ocrQualityMinConfidence) : null,
        ocrQualityMinWordCount: toBoolean(seriesFormData.ocrRequired) ? parseIntOrNull(seriesFormData.ocrQualityMinWordCount) : null,
        ocrQualityMinTextCoverage: toBoolean(seriesFormData.ocrRequired) ? parseFloatOrNull(seriesFormData.ocrQualityMinTextCoverage) : null,
        ocrQualityPageCountMin: toBoolean(seriesFormData.ocrRequired) ? parseIntOrNull(seriesFormData.ocrQualityPageCountMin) : null,
        ocrQualityPageCountMax: toBoolean(seriesFormData.ocrRequired) ? parseIntOrNull(seriesFormData.ocrQualityPageCountMax) : null,
        ocrQualityAutoReject: toBoolean(seriesFormData.ocrRequired) ? toBoolean(seriesFormData.ocrQualityAutoReject) : false,
        // Parametrización Firma Digital (#59): solo enviar valores efectivos si signatureRequired=true.
        signatureIndividualAllowed: toBoolean(seriesFormData.signatureRequired) ? toBoolean(seriesFormData.signatureIndividualAllowed) : true,
        signatureBatchAllowed: toBoolean(seriesFormData.signatureRequired) ? toBoolean(seriesFormData.signatureBatchAllowed) : false,
        preserveOriginalBinary: toBoolean(seriesFormData.signatureRequired) ? toBoolean(seriesFormData.preserveOriginalBinary) : true,
        certMatchStrategy: toBoolean(seriesFormData.signatureRequired) ? (seriesFormData.certMatchStrategy || null) : null,
        revocationCheck: toBoolean(seriesFormData.signatureRequired) ? (seriesFormData.revocationCheck || 'STRICT') : 'STRICT',
        padesLevel: toBoolean(seriesFormData.signatureRequired) ? (seriesFormData.padesLevel || 'B-T') : 'B-T',
        isSeries: true,
        state: 1
      };

      // Guardar propiedades de la serie
      await documentTypeService.updateTipoDocumento(seriesFormData.id, apiPayload);

      // Actualizar política de retención si cambió
      if (activePolicy !== initialActivePolicy) {
        try {
          if (initialActivePolicy) await retentionPolicyService.dissociatePolicy(initialActivePolicy, seriesFormData.id);
          if (activePolicy) await retentionPolicyService.associatePolicy(activePolicy, seriesFormData.id);
        } catch (e) {
          notify.error('Error al actualizar la asociación de la política.');
        }
      }

      // Guardar campos de metadata
      await documentTypeService.updateSpec(seriesFormData.id, especificacion);

      // Recargar spec desde el servidor
      const response = await documentTypeService.getSpec(seriesFormData.id);
      const normalizedSpec = {
        ...(response.data || {}),
        fieldSpec: (response.data?.fieldSpec || []).map(normalizeFieldSpec)
      };
      setEspecificacion(normalizedSpec);
      setHistorial([{
        especificacion: JSON.parse(JSON.stringify(normalizedSpec)),
        valoresModificados: new Map(),
        timestamp: Date.now()
      }]);
      setIndiceHistorial(0);

      await cargarTiposDocumentos();
      setInitialActivePolicy(activePolicy);
      setCambiosNoGuardados(false);
      setValoresModificados(new Map());
      setSeriesDirty(false);
      notify.success('Serie guardada exitosamente');
    } catch (err) {
      console.error('Error guardando serie:', err);
      if (err.response?.status === 400 && err.response?.data?.validationErrors) {
        err.response.data.validationErrors.forEach(e => notify.error(e.message));
      } else {
        notify.error('Error al guardar: ' + (err.response?.data?.message || err.message));
      }
    } finally {
      setGuardando(false);
    }
  };

  const getTipoDisplay = (tipo) => {
    const tipos = {
      'STRING': 'Texto',
      'INTEGER': 'Entero',
      'LONG': 'Entero Largo',
      'DATE': 'Fecha'
    };
    return tipos[tipo] || tipo;
  };

  const renderTotalBadge = (count) => {
    const isLoading = count === undefined;
    const isZero = count === 0;
    const badgeStyle = isZero || isLoading
      ? { backgroundColor: '#E0E0E0', color: '#757575', border: '1px solid #BDBDBD' }
      : { backgroundColor: '#E8F5E9', color: '#2E7D32', border: '1px solid #C8E6C9' };
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 10,
        padding: '2px 6px',
        borderRadius: 10,
        marginLeft: 4,
        ...badgeStyle
      }}>
        
        <GrDocument size={8} /> {isLoading ? '...' : count}
      </span>
    );
  };

  const renderBadge = (enabled, label) => {
    if (!enabled) return null;
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 10,
        padding: '2px 6px',
        borderRadius: 10,
        backgroundColor: '#E8F5E9',
        color: '#2E7D32',
        marginLeft: 4,
        border: '1px solid #C8E6C9'
      }}>
        <FaCheck size={8} /> {label}
      </span>
    );
  };

  // Árbol unificado para la sidebar: folders (arbolNodes) + series (tiposDocumentos)
  // anidados según `parent`/`padre`. Memoizado para no recalcular en cada render.
  const seriesTree = React.useMemo(
    () => buildTreeFromFlat(arbolNodes, tiposDocumentos),
    [arbolNodes, tiposDocumentos]
  );

  // Path completo (raíz → serie) para el nodo seleccionado actualmente.
  // Lo necesita DocTreeSidebar para auto-expandir ancestros y resaltar la serie.
  const selectedTreePath = React.useMemo(() => {
    if (!tipoSeleccionado) return [];
    return findTreePath(seriesTree, tipoSeleccionado) || [];
  }, [tipoSeleccionado, seriesTree]);

  // Click en el árbol: si es serie → seleccionar (como antes); si es folder → solo
  // expand/collapse (lo maneja internamente DocTreeSidebar antes de invocar este
  // callback, por eso acá ignoramos folder clicks).
  const handleTreeNodeClick = (path) => {
    if (!path || path.length === 0) return;
    const last = path[path.length - 1];
    if (last.isSeries) {
      cargarEspecificacion(last.id);
    }
  };

  // ─── Folder CRUD (#57 follow-up) ────────────────────────────────────────
  const [folderCreateOpen, setFolderCreateOpen] = useState(false);
  const [folderCreateParent, setFolderCreateParent] = useState(null);
  const [folderEditOpen, setFolderEditOpen] = useState(false);
  const [folderEditing, setFolderEditing] = useState(null);

  const handleFolderCreate = (node) => {
    // Si el caller pasó un nodo, se crea un subfolder bajo ese folder. Si no
    // (ej. botón "Nuevo folder raíz"), parent queda null.
    setFolderCreateParent(node ? node.id : null);
    setFolderCreateOpen(true);
  };

  const handleFolderEdit = (node) => {
    if (!node) return;
    // El padre real vive en `_raw._data.parent` (la cadena de envoltorios es:
    // toArboristData → buildTreeFromFlat → folder original del API).
    // Fallback a un lookup directo en arbolNodes por si algún campo no está.
    const original = arbolNodes.find(n => String(n.id) === String(node.id));
    const currentParent = node._raw?._data?.parent ?? original?.parent ?? null;
    setFolderEditing({ id: node.id, name: node.name, parent: currentParent });
    setFolderEditOpen(true);
  };

  const handleFolderDelete = async (node) => {
    if (!node) return;
    const ok = await confirm({
      title: 'Eliminar folder',
      message: `¿Eliminar el folder "${node.name}" (${node.id})? Solo se puede borrar si está vacío (sin folders ni series adentro).`,
      confirmText: 'Eliminar',
      destructive: true,
    });
    if (!ok) return;
    try {
      await documentTypeService.deleteFolder(node.id);
      notify.success(`Folder "${node.name}" eliminado`);
      // Recargar el árbol de folders.
      const response = await documentTypeService.getFolders();
      setArbolNodes(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      notify.error(`No se pudo eliminar el folder: ${detail}`);
    }
  };

  const submitFolderCreate = async ({ id, name, parent }) => {
    try {
      await documentTypeService.createFolder({ id, name, parent });
      notify.success(`Folder "${name}" creado`);
      setFolderCreateOpen(false);
      const response = await documentTypeService.getFolders();
      setArbolNodes(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      notify.error(`No se pudo crear el folder: ${detail}`);
    }
  };

  const submitFolderEdit = async ({ id, name, parent, nameChanged, parentChanged }) => {
    try {
      if (nameChanged) {
        await documentTypeService.renameFolder(id, name);
      }
      if (parentChanged) {
        await documentTypeService.moveFolder(id, parent);
      }
      notify.success(`Folder "${name}" actualizado`);
      setFolderEditOpen(false);
      const response = await documentTypeService.getFolders();
      setArbolNodes(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      notify.error(`No se pudo actualizar el folder: ${detail}`);
    }
  };

  // Agrupar campos por origin
  const agruparCamposPorOrigin = (campos) => {
    const grouped = {};
    campos.forEach(campo => {
      const origin = campo.origin?.trim()?.toUpperCase() || 'OTROS';
      if (!grouped[origin]) {
        grouped[origin] = [];
      }
      grouped[origin].push(campo);
    });
    return grouped;
  };

  // Función para formatear el nombre del origen para mostrar
  const formatOriginName = (origin) => {
    const originMap = {
      'CSV': 'CSV',
      'OCR': 'OCR',
      'OTROS': 'Otros',
      'MANUAL': 'Manual',
      'SISTEMA': 'Sistema'
    };
    return originMap[origin] || origin;
  };

  // Ordenar los orígenes: CSV primero, luego OCR, luego el resto alfabéticamente
  const ordenarOrigenes = (origenes) => {
    return origenes.sort((a, b) => {
      const order = { 'CSV': 1, 'OCR': 2, 'OTROS': 99 };
      const orderA = order[a] || 50;
      const orderB = order[b] || 50;
      if (orderA !== orderB) return orderA - orderB;
      return a.localeCompare(b);
    });
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.titleSection}>
          <AiOutlineDatabase style={styles.titleIcon} />
          <h1 style={styles.title}>Gestión de Series Documentales</h1>
        </div>
        {onBack && (
          <button
            style={styles.backButton}
            onClick={onBack}
          >
            <AiOutlineClose style={styles.backIcon} />
            Cerrar
          </button>
        )}
      </div>

      {error && (
        <div style={styles.errorMessage}>
          {error}
        </div>
      )}

      <div style={styles.content}>
        {/* Lista de tipos de documentos */}
        <div style={styles.section}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
            <h2 style={styles.sectionTitle}>Series Documentales</h2>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                title="Importar definición de serie desde un archivo JSON"
                aria-label="Importar serie documental"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 12px',
                  backgroundColor: 'transparent',
                  color: '#00468B',
                  border: '1px solid #C7DBF0',
                  borderRadius: 4,
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease, border-color 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#EFF6FF';
                  e.currentTarget.style.borderColor = '#00468B';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = '#C7DBF0';
                }}
              >
                <AiOutlineImport size={15} /> Importar
              </button>
              <button
                style={{ ...styles.addButton, padding: '6px 12px', fontSize: 13, backgroundColor: 'transparent', color: '#00468B', border: '1px solid #C7DBF0' }}
                onClick={() => handleFolderCreate(null)}
                title="Crear un folder en la raíz del árbol"
                data-testid="btn-new-folder"
              >
                <AiOutlinePlus style={styles.buttonIcon} /> Nuevo Folder
              </button>
              <button
                style={{ ...styles.addButton, padding: '6px 12px', fontSize: 13 }}
                onClick={handleCreateNew}
              >
                <AiOutlinePlus style={styles.buttonIcon} /> Nueva Serie
              </button>
            </div>
          </div>

          {loading ? (
            <div style={styles.loading}>Cargando series...</div>
          ) : (
            <div style={styles.treeWrapper} data-testid="series-tree-wrapper">
              <DocTreeSidebar
                folders={seriesTree}
                selectedPath={selectedTreePath}
                onFolderClick={handleTreeNodeClick}
                title=""
                storageKey="boveda.series-admin.expanded"
                noFetchCounts
                pillsPosition="below"
                emptyMessage="No hay series ni carpetas"
                containerStyle={{ width: '100%', borderRight: 'none', backgroundColor: '#FFFFFF', padding: '12px' }}
                folderActions={{
                  onCreate: handleFolderCreate,
                  onEdit: handleFolderEdit,
                  onDelete: handleFolderDelete,
                }}
                renderSeriePills={(serieId, raw) => {
                  // `raw` viene del nodo original (con _data.tipo). Si no, fallback al lookup por id.
                  const tipo = (raw && raw._data) || tiposDocumentos.find(t => String(t.id) === String(serieId));
                  if (!tipo) return null;
                  return (
                    <>
                      {renderTotalBadge(serieCounts[serieId])}
                      {renderBadge(tipo.ocrRequired, 'OCR')}
                      {renderBadge(tipo.reviewRequired, 'Review')}
                      {renderBadge(tipo.approvalRequired, 'Approval')}
                      {renderBadge(tipo.signatureRequired, 'Firma')}
                    </>
                  );
                }}
              />
            </div>
          )}
        </div>

        {/* Especificación de metadatos + Propiedades de la Serie */}
        {(especificacion || mode === 'create') && (
          <div style={styles.section}>
            {/* SECCIÓN A: PROPIEDADES DE LA SERIE */}
            <div style={{ marginBottom: 30, borderBottom: '1px solid #eee', paddingBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: showProperties ? 15 : 0 }}>
                <div
                  style={{ ...styles.specHeader, cursor: 'pointer', flex: 1, margin: 0 }}
                  onClick={() => setShowProperties(!showProperties)}
                >
                  <h2 style={styles.sectionTitle} data-testid="series-properties-title">
                    {showProperties ? '▼' : '▶'}{' '}
                    {mode === 'create'
                      ? <>Nueva serie <span style={{ fontSize: '0.7em', color: '#666', fontWeight: 400 }}>· sin guardar</span></>
                      : (seriesFormData.descripcion || seriesFormData.id || 'Serie sin nombre')}
                  </h2>
                </div>
                {mode === 'edit-series' && (
                  <button
                    type="button"
                    onClick={handleExportSerie}
                    disabled={exportingSeries}
                    title="Exportar definición de esta serie como JSON"
                    aria-label="Exportar definición de serie"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '6px 12px',
                      backgroundColor: 'transparent',
                      color: exportingSeries ? '#9CA3AF' : '#00468B',
                      border: `1px solid ${exportingSeries ? '#E5E7EB' : '#C7DBF0'}`,
                      borderRadius: 4,
                      fontSize: 13,
                      fontWeight: 500,
                      cursor: exportingSeries ? 'wait' : 'pointer',
                      transition: 'background-color 0.2s ease, border-color 0.2s ease',
                      flexShrink: 0,
                    }}
                    onMouseEnter={(e) => {
                      if (exportingSeries) return;
                      e.currentTarget.style.backgroundColor = '#EFF6FF';
                      e.currentTarget.style.borderColor = '#00468B';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.borderColor = exportingSeries ? '#E5E7EB' : '#C7DBF0';
                    }}
                  >
                    <AiOutlineExport size={15} />
                    {exportingSeries ? 'Exportando…' : 'Exportar'}
                  </button>
                )}
                {mode === 'edit-series' && (() => {
                  const hasChanges = seriesDirty || cambiosNoGuardados || activePolicy !== initialActivePolicy;
                  const disabled = guardando || !hasChanges;
                  return (
                    <button
                      type="button"
                      onClick={disabled ? undefined : guardarTodo}
                      disabled={disabled}
                      title={hasChanges ? 'Guardar todos los cambios' : 'Sin cambios para guardar'}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '6px 12px',
                        backgroundColor: disabled ? 'transparent' : '#00468B',
                        color: disabled ? '#9CA3AF' : '#fff',
                        border: `1px solid ${disabled ? '#E5E7EB' : '#00468B'}`,
                        borderRadius: 4,
                        fontSize: 13,
                        fontWeight: 500,
                        cursor: disabled ? 'default' : 'pointer',
                        flexShrink: 0,
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <AiOutlineSave size={15} />
                      {guardando ? 'Guardando…' : 'Guardar'}
                    </button>
                  );
                })()}
                {mode === 'create' && (() => {
                  // Habilitar el botón solo cuando todos los campos obligatorios están
                  // completos y el ID cumple la política de nombrado. Evita el roundtrip
                  // de validación al backend con un click "fácil de equivocar".
                  const idInvalid = validateIdentifier(seriesFormData.id, 'ID de la serie') !== null;
                  const descripcionMissing = !seriesFormData.descripcion?.trim();
                  const padreMissing = !seriesFormData.padre?.trim();
                  const canCreate = !idInvalid && !descripcionMissing && !padreMissing;
                  const disabled = savingSeries || !canCreate;
                  let title;
                  if (savingSeries) title = 'Creando…';
                  else if (idInvalid) title = 'Completá un ID válido para habilitar';
                  else if (descripcionMissing) title = 'Completá la descripción para habilitar';
                  else if (padreMissing) title = 'Seleccioná el nodo padre para habilitar';
                  else title = 'Crear la serie con los datos ingresados';
                  return (
                    <button
                      type="button"
                      onClick={disabled ? undefined : handleSaveSeries}
                      disabled={disabled}
                      title={title}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '6px 12px',
                        backgroundColor: disabled ? 'transparent' : '#00468B',
                        color: disabled ? '#9CA3AF' : '#fff',
                        border: `1px solid ${disabled ? '#E5E7EB' : '#00468B'}`,
                        borderRadius: 4,
                        fontSize: 13,
                        fontWeight: 500,
                        cursor: disabled ? 'default' : 'pointer',
                        flexShrink: 0,
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <AiOutlineSave size={15} />
                      {savingSeries ? 'Creando…' : 'Crear Serie'}
                    </button>
                  );
                })()}
              </div>

              {showProperties && (
                <div style={{ padding: '0 10px' }}>
                  <div style={styles.formRow}>
                    <div style={styles.formField}>
                      <label style={styles.label}>ID Serie (Único)</label>
                      <input
                        type="text"
                        value={seriesFormData.id}
                        onChange={(e) => {
                          updateSeriesForm({ ...seriesFormData, id: e.target.value.toUpperCase() });
                          // Mientras escribe limpiamos el error visible — se vuelve a evaluar al perder foco
                          if (seriesIdError) setSeriesIdError(null);
                        }}
                        onBlur={(e) => {
                          // Solo validar en alta; en edit el ID está disabled
                          if (mode === 'create') {
                            setSeriesIdError(validateIdentifier(e.target.value, 'ID de la serie'));
                          }
                        }}
                        style={{
                          ...styles.input,
                          backgroundColor: mode === 'edit-series' ? '#f0f0f0' : 'white',
                          borderColor: seriesIdError ? '#DC2626' : (styles.input.borderColor || '#ccc'),
                        }}
                        disabled={mode === 'edit-series'}
                        placeholder="EJ: FACTURAS_PROVEEDORES"
                        aria-invalid={!!seriesIdError}
                        aria-describedby="series-id-hint"
                      />
                      {seriesIdError && (
                        <span style={{ color: '#DC2626', fontSize: 12, marginTop: 4, display: 'block' }}>
                          {seriesIdError}
                        </span>
                      )}
                      <span id="series-id-hint" style={{ color: '#666', fontSize: 12, marginTop: 4, display: 'block' }}>
                        {IDENTIFIER_POLICY_HINT}
                      </span>
                    </div>
                    <div style={styles.formField}>
                      <label style={styles.label}>Descripción</label>
                      <input
                        type="text"
                        value={seriesFormData.descripcion}
                        onChange={(e) => updateSeriesForm({ ...seriesFormData, descripcion: e.target.value })}
                        style={styles.input}
                        placeholder="Ej: Facturas recibidas de proveedores"
                      />
                    </div>
                  </div>

                  <div style={styles.formRow}>
                    <div style={styles.formField}>
                      <label style={styles.label}>Nodo Padre</label>
                      <FolderTreePicker
                        folders={arbolNodes}
                        value={seriesFormData.padre}
                        onChange={(id) => updateSeriesForm({ ...seriesFormData, padre: id })}
                        disabled={mode === 'edit-series'}
                        placeholder="Seleccionar nodo padre…"
                      />
                    </div>
                    <div style={styles.formField}>
                      <label style={styles.label}>Orden de Visualización</label>
                      <input
                        type="number"
                        value={seriesFormData.orden}
                        onChange={(e) => updateSeriesForm({ ...seriesFormData, orden: parseInt(e.target.value) || 0 })}
                        style={styles.input}
                      />
                    </div>
                  </div>

                  <div style={{ marginTop: 15 }}>
                    <label style={styles.label}>Configuración de Workflow</label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
                      <label style={styles.checkboxLabel}>
                        <input
                          type="checkbox"
                          checked={seriesFormData.ocrRequired}
                          onChange={(e) => updateSeriesForm({ ...seriesFormData, ocrRequired: e.target.checked })}
                          style={styles.checkbox}
                        />
                        <strong>Requiere OCR:</strong> El documento pasará por proceso de reconocimiento de texto.
                      </label>

                      <label style={styles.checkboxLabel}>
                        <input
                          type="checkbox"
                          checked={seriesFormData.reviewRequired}
                          onChange={(e) => updateSeriesForm({ ...seriesFormData, reviewRequired: e.target.checked })}
                          style={styles.checkbox}
                        />
                        <strong>Requiere Revisión:</strong> Un revisor debe validar el documento.
                      </label>

                      <label style={styles.checkboxLabel}>
                        <input
                          type="checkbox"
                          checked={seriesFormData.approvalRequired}
                          onChange={(e) => updateSeriesForm({ ...seriesFormData, approvalRequired: e.target.checked })}
                          style={styles.checkbox}
                        />
                        <strong>Requiere Aprobación:</strong> Un aprobador debe dar el visto bueno final.
                      </label>

                      <label style={styles.checkboxLabel}>
                        <input
                          type="checkbox"
                          checked={seriesFormData.signatureRequired}
                          onChange={(e) => updateSeriesForm({ ...seriesFormData, signatureRequired: e.target.checked })}
                          style={styles.checkbox}
                        />
                        <strong>Requiere Firma Digital:</strong> los documentos deben ser firmados digitalmente antes de quedar publicados.
                      </label>

                    </div>
                  </div>

                  {seriesFormData.ocrRequired && (
                    <div style={{ marginTop: 15, paddingTop: 15, borderTop: '1px solid #eee' }}>
                      <label style={styles.label}>Parametrización OCR</label>
                      <p style={{ fontSize: '12px', color: '#666', marginTop: 4, marginBottom: 8 }}>
                        Configuración del pipeline OCR + AI para esta serie. Todos los campos son opcionales — dejar vacío usa defaults globales.
                      </p>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 8 }}>
                        <div style={styles.formField}>
                          <label style={styles.label}>Máx. páginas para parseo fino</label>
                          <input
                            type="number" min="1" placeholder="sin límite"
                            value={seriesFormData.ocrMaxFinePages}
                            onChange={(e) => updateSeriesForm({ ...seriesFormData, ocrMaxFinePages: e.target.value })}
                            style={styles.input}
                          />
                          <p style={{ fontSize: '11px', color: '#888' }}>Limita las primeras N páginas enviadas a Vision AI (ahorra costos y tiempo).</p>
                        </div>
                      </div>

                      <div style={{ marginTop: 12 }}>
                        <label style={styles.checkboxLabel}>
                          <input
                            type="checkbox"
                            checked={seriesFormData.ocrQualityEnabled}
                            onChange={(e) => updateSeriesForm({ ...seriesFormData, ocrQualityEnabled: e.target.checked })}
                            style={styles.checkbox}
                          />
                          <strong>Validar calidad OCR:</strong> Aplicar umbrales de confianza, palabras y páginas.
                        </label>
                      </div>

                      {seriesFormData.ocrQualityEnabled && (
                        <>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 10 }}>
                            <div style={styles.formField}>
                              <label style={styles.label}>Confianza mínima (0.0–1.0)</label>
                              <input
                                type="number" min="0" max="1" step="0.01" placeholder="ej: 0.60"
                                value={seriesFormData.ocrQualityMinConfidence}
                                onChange={(e) => updateSeriesForm({ ...seriesFormData, ocrQualityMinConfidence: e.target.value })}
                                style={styles.input}
                              />
                            </div>
                            <div style={styles.formField}>
                              <label style={styles.label}>Mín. palabras</label>
                              <input
                                type="number" min="0" placeholder="ej: 10"
                                value={seriesFormData.ocrQualityMinWordCount}
                                onChange={(e) => updateSeriesForm({ ...seriesFormData, ocrQualityMinWordCount: e.target.value })}
                                style={styles.input}
                              />
                            </div>
                            <div style={styles.formField}>
                              <label style={styles.label}>Cobertura mín. (0.0–1.0)</label>
                              <input
                                type="number" min="0" max="1" step="0.01" placeholder="ej: 0.01"
                                value={seriesFormData.ocrQualityMinTextCoverage}
                                onChange={(e) => updateSeriesForm({ ...seriesFormData, ocrQualityMinTextCoverage: e.target.value })}
                                style={styles.input}
                              />
                            </div>
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                            <div style={styles.formField}>
                              <label style={styles.label}>Mín. páginas del doc</label>
                              <input
                                type="number" min="1" placeholder="sin mínimo"
                                value={seriesFormData.ocrQualityPageCountMin}
                                onChange={(e) => updateSeriesForm({ ...seriesFormData, ocrQualityPageCountMin: e.target.value })}
                                style={styles.input}
                              />
                            </div>
                            <div style={styles.formField}>
                              <label style={styles.label}>Máx. páginas del doc</label>
                              <input
                                type="number" min="1" placeholder="sin máximo"
                                value={seriesFormData.ocrQualityPageCountMax}
                                onChange={(e) => updateSeriesForm({ ...seriesFormData, ocrQualityPageCountMax: e.target.value })}
                                style={styles.input}
                              />
                            </div>
                          </div>

                          <div style={{ marginTop: 12 }}>
                            <label style={styles.checkboxLabel}>
                              <input
                                type="checkbox"
                                checked={seriesFormData.ocrQualityAutoReject}
                                onChange={(e) => updateSeriesForm({ ...seriesFormData, ocrQualityAutoReject: e.target.checked })}
                                style={styles.checkbox}
                              />
                              <strong>Auto-rechazo:</strong> Si un documento no pasa los umbrales, rechazarlo automáticamente.
                            </label>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {seriesFormData.signatureRequired && (
                    <div style={{ marginTop: 15, paddingTop: 15, borderTop: '1px solid #eee' }}>
                      <label style={styles.label}>Configuración de Firma Digital</label>
                      <p style={{ fontSize: '12px', color: '#666', marginTop: 4, marginBottom: 8 }}>
                        Cuando esté activa, los documentos de esta serie requerirán una firma digital antes de quedar publicados. La firma se realiza después de la aprobación, usando un certificado digital del firmante.
                      </p>

                      {/* Modalidades */}
                      <div style={{ marginTop: 10 }}>
                        <label style={{ ...styles.label, fontSize: 12 }}>Formas de firma habilitadas</label>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 6 }}>
                          <label style={styles.checkboxLabel}>
                            <input
                              type="checkbox"
                              checked={seriesFormData.signatureIndividualAllowed}
                              onChange={(e) => updateSeriesForm({ ...seriesFormData, signatureIndividualAllowed: e.target.checked })}
                              style={styles.checkbox}
                            />
                            <strong>Firma de a un documento:</strong> el firmante abre el documento, lo revisa y lo firma de a uno por vez.
                          </label>
                          <label style={styles.checkboxLabel}>
                            <input
                              type="checkbox"
                              checked={seriesFormData.signatureBatchAllowed}
                              onChange={(e) => updateSeriesForm({ ...seriesFormData, signatureBatchAllowed: e.target.checked })}
                              style={styles.checkbox}
                            />
                            <strong>Firma de varios juntos:</strong> el firmante selecciona un grupo de documentos y los firma todos con una sola operación. Más rápido para volúmenes grandes.
                          </label>
                        </div>
                        {!seriesFormData.signatureIndividualAllowed && !seriesFormData.signatureBatchAllowed && (
                          <p style={{ fontSize: '11px', color: '#DC2626', marginTop: 4 }}>
                            Tiene que haber al menos una forma de firma habilitada.
                          </p>
                        )}
                      </div>

                      {/* Preservación + cert match */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
                        <div style={{ ...styles.formField }}>
                          <label style={styles.checkboxLabel}>
                            <input
                              type="checkbox"
                              checked={seriesFormData.preserveOriginalBinary}
                              onChange={(e) => updateSeriesForm({ ...seriesFormData, preserveOriginalBinary: e.target.checked })}
                              style={styles.checkbox}
                            />
                            <strong>Conservar el documento original</strong>
                          </label>
                          <p style={{ fontSize: '11px', color: '#888' }}>
                            Guarda una copia del documento original junto a la versión firmada. Si lo desactivás, sólo se conserva la versión firmada.
                          </p>
                        </div>
                        <div style={styles.formField}>
                          <label style={styles.label}>Identidad del firmante</label>
                          <select
                            value={seriesFormData.certMatchStrategy}
                            onChange={(e) => updateSeriesForm({ ...seriesFormData, certMatchStrategy: e.target.value })}
                            style={styles.select}
                          >
                            <option value="">Usar la configuración general del sistema</option>
                            <option value="NONE">No validar identidad del firmante</option>
                            <option value="SUBJECT_CN_EQUALS_USERNAME">El nombre del certificado debe coincidir con el usuario</option>
                            <option value="SUBJECT_CUIT_EQUALS_USER_CLAIM">El CUIT del certificado debe coincidir con el del usuario</option>
                            <option value="SUBJECT_CUIL_EQUALS_USER_CLAIM">El CUIL del certificado debe coincidir con el del usuario</option>
                            <option value="ISSUER_AND_SUBJECT_DN_LITERAL">Coincidencia exacta de los datos del certificado</option>
                          </select>
                          <p style={{ fontSize: '11px', color: '#888' }}>
                            Cómo se verifica que el certificado pertenece a la persona que está firmando desde {ENV.APP_NAME}.
                          </p>
                        </div>
                      </div>

                      {/* Revocación + nivel PAdES */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
                        <div style={styles.formField}>
                          <label style={styles.label}>Verificación del certificado al firmar</label>
                          <select
                            value={seriesFormData.revocationCheck}
                            onChange={(e) => updateSeriesForm({ ...seriesFormData, revocationCheck: e.target.value })}
                            style={styles.select}
                          >
                            <option value="STRICT">Verificación estricta — recomendada</option>
                            <option value="LENIENT">Verificación flexible</option>
                            <option value="OFF">Sin verificación</option>
                          </select>
                          <p style={{ fontSize: '11px', color: '#888' }}>
                            {seriesFormData.revocationCheck === 'STRICT'
                              && 'Antes de aceptar la firma, se consulta a la autoridad que emitió el certificado para confirmar que sigue vigente. Si no se puede consultar, la firma se rechaza.'}
                            {seriesFormData.revocationCheck === 'LENIENT'
                              && 'Se intenta consultar a la autoridad que emitió el certificado; si no responde, la firma se acepta igual y queda registrada para revisión posterior.'}
                            {seriesFormData.revocationCheck === 'OFF'
                              && 'No se consulta el estado del certificado. Sólo recomendable para uso interno donde no se requiere validez legal externa.'}
                          </p>
                        </div>
                        <div style={styles.formField}>
                          <label style={styles.label}>Tipo de firma a generar</label>
                          <select
                            value={seriesFormData.padesLevel}
                            onChange={(e) => updateSeriesForm({ ...seriesFormData, padesLevel: e.target.value })}
                            style={styles.select}
                          >
                            <option value="B-B">Firma básica</option>
                            <option value="B-T">Firma con fecha y hora certificada — recomendada</option>
                            <option value="B-LT">Firma para conservación a largo plazo</option>
                            <option value="B-LTA">Firma para archivo permanente</option>
                          </select>
                          <p style={{ fontSize: '11px', color: '#888' }}>
                            {seriesFormData.padesLevel === 'B-B'
                              && 'Sólo registra el certificado del firmante en el documento.'}
                            {seriesFormData.padesLevel === 'B-T'
                              && 'Agrega una marca de tiempo certificada por una autoridad externa, que prueba el momento exacto de la firma.'}
                            {seriesFormData.padesLevel === 'B-LT'
                              && 'Incluye en el documento toda la información necesaria para validar la firma muchos años después, incluso si la autoridad certificante deja de operar.'}
                            {seriesFormData.padesLevel === 'B-LTA'
                              && 'Igual que "conservación a largo plazo", pero con renovaciones automáticas para preservación documental indefinida.'}
                          </p>
                        </div>
                      </div>

                      {seriesFormData.revocationCheck === 'OFF'
                        && (seriesFormData.padesLevel === 'B-LT' || seriesFormData.padesLevel === 'B-LTA') && (
                        <p style={{ fontSize: '11px', color: '#DC2626', marginTop: 8 }}>
                          Esta combinación no es válida: las firmas para conservación a largo plazo o archivo permanente requieren verificar el certificado al firmar.
                        </p>
                      )}
                    </div>
                  )}

                  <div style={{ marginTop: 15, paddingTop: 15, borderTop: '1px solid #eee' }}>
                    <label style={styles.label}>Política de Retención Documental</label>
                    <div style={{ marginTop: 5 }}>
                      <select
                        style={{ ...styles.select, width: '100%', maxWidth: '400px' }}
                        value={activePolicy}
                        onChange={(e) => { setActivePolicy(e.target.value); setSeriesDirty(true); }}
                      >
                        <option value="">-- Sin política asignada --</option>
                        {policies.map(p => (
                          <option key={p.idPolicy} value={p.idPolicy}>{p.name} ({p.retentionPeriodDays} días)</option>
                        ))}
                      </select>
                      <p style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                         Asocia una política para gestionar el ciclo de vida y la retención de los documentos de esta serie.
                      </p>
                    </div>
                  </div>

                </div>
              )}
            </div>

            {/* SECCIÓN B: ESPECIFICACIONES DE METADATOS */}
            {tipoSeleccionado ? (
              <>
                <div style={styles.specHeader}>
                  <h2 style={styles.sectionTitle}>
                    Especificaciones de Metadatos
                  </h2>
                  <div style={styles.headerActions}>
                    <div style={styles.undoRedoContainer}>
                      <button
                        style={{
                          ...styles.undoRedoButton,
                          ...(indiceHistorial <= 0 ? styles.undoRedoButtonDisabled : {})
                        }}
                        onClick={undo}
                        disabled={indiceHistorial <= 0}
                        title="Deshacer"
                      >
                        <AiOutlineUndo size={14} />
                      </button>
                      <button
                        style={{
                          ...styles.undoRedoButton,
                          ...(indiceHistorial >= historial.length - 1 ? styles.undoRedoButtonDisabled : {})
                        }}
                        onClick={redo}
                        disabled={indiceHistorial >= historial.length - 1}
                        title="Rehacer"
                      >
                        <AiOutlineRedo size={14} />
                      </button>
                      <button
                        style={styles.resetButton}
                        onClick={reset}
                        title="Resetear a estado original"
                      >
                        <AiOutlineStop size={14} />
                      </button>
                    </div>
                    <button
                      style={styles.addButton}
                      onClick={() => setEditando(true)}
                    >
                      <AiOutlinePlus style={styles.buttonIcon} />
                      Agregar Campo
                    </button>
                  </div>
                </div>

                {/* Formulario para nuevo campo */}
                {editando && !campoEditando && (
                  <div style={styles.formContainer}>
                    <h3 style={styles.formTitle}>Agregar Nuevo Campo</h3>
                    <div style={styles.formRow}>
                      <div style={styles.formField}>
                        <label style={styles.label}>Nombre del Campo</label>
                        <input
                          type="text"
                          value={nuevoCampo.nombre}
                          onChange={(e) => setNuevoCampo({ ...nuevoCampo, nombre: e.target.value })}
                          style={styles.input}
                          placeholder="ej: materia"
                        />
                        <span style={styles.fieldHint}>
                          Usar ID técnico: letras, números, "_" o "-" (sin espacios, máx. 50).
                        </span>
                      </div>
                      <div style={styles.formField}>
                        <label style={styles.label}>Etiqueta</label>
                        <input
                          type="text"
                          value={nuevoCampo.label}
                          onChange={(e) => setNuevoCampo({ ...nuevoCampo, label: e.target.value })}
                          style={styles.input}
                          placeholder="ej: Materia"
                        />
                      </div>
                    </div>
                    <div style={styles.formRow}>
                      <div style={styles.formField}>
                        <label style={styles.label}>Tipo de Dato</label>
                        <select
                          value={nuevoCampo.tipo}
                          onChange={(e) => setNuevoCampo({ ...nuevoCampo, tipo: e.target.value })}
                          style={styles.select}
                        >
                          <option value="STRING">Texto</option>
                          <option value="INTEGER">Entero</option>
                          <option value="LONG">Entero Largo</option>
                          <option value="DATE">Fecha</option>
                        </select>
                      </div>
                      {nuevoCampo.tipo === 'DATE' && (
                        <div style={styles.formField}>
                          <label style={styles.label}>Máscaras de fecha</label>
                          <MaskEditor
                            value={nuevoCampo.mask}
                            onChange={(val) => setNuevoCampo({ ...nuevoCampo, mask: val })}
                          />
                        </div>
                      )}
                    </div>
                    {/* Fuentes de entrada (reemplaza el viejo selector de "Origen") */}
                    <div style={styles.formRow}>
                      <div style={{ ...styles.formField, gridColumn: '1 / -1' }}>
                        <label style={styles.label}>Fuentes de entrada</label>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                          {[
                            { key: 'CSV_POSITIONAL', label: 'CSV', color: '#2563EB', bg: '#EFF6FF' },
                            { key: 'OCR_AI', label: 'OCR', color: '#D97706', bg: '#FFFBEB' },
                            { key: 'MANUAL', label: 'Manual', color: '#059669', bg: '#ECFDF5' },
                          ].map(({ key, label, color, bg }) => {
                            const strategies = nuevoCampo.inputStrategies || [];
                            const isActive = strategies.some(s => s.strategyType === key);
                            return (
                              <label key={key} style={{
                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                padding: '6px 14px', borderRadius: 20, cursor: 'pointer',
                                border: `2px solid ${isActive ? color : '#E5E7EB'}`,
                                backgroundColor: isActive ? bg : '#fff',
                                color: isActive ? color : '#9CA3AF',
                                fontWeight: 600, fontSize: '0.82rem',
                                transition: 'all 0.15s ease',
                              }}>
                                <input type="checkbox" checked={isActive} style={{ display: 'none' }}
                                  onChange={(e) => {
                                    let updated;
                                    if (e.target.checked) {
                                      const config = key === 'CSV_POSITIONAL'
                                        ? { position: (especificacion?.fieldSpec?.length || 0) + 1 }
                                        : {};
                                      updated = [...strategies, { strategyType: key, config }];
                                    } else {
                                      updated = strategies.filter(s => s.strategyType !== key);
                                    }
                                    setNuevoCampo({ ...nuevoCampo, inputStrategies: updated });
                                  }} />
                                {label}
                              </label>
                            );
                          })}
                        </div>
                        <span style={styles.fieldHint}>Seleccioná una o más fuentes por donde se cargará este campo.</span>
                      </div>
                    </div>
                    {/* Checkboxes en grid 2 cols, mismo layout que "Configuración de Workflow" */}
                    <div style={styles.formRow}>
                      <div style={{ ...styles.formField, gridColumn: '1 / -1' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                          <label style={styles.checkboxLabel}>
                            <input type="checkbox" checked={nuevoCampo.required}
                              onChange={(e) => setNuevoCampo({ ...nuevoCampo, required: e.target.checked })}
                              style={styles.checkbox} />
                            <strong>Requerido:</strong> Debe estar presente para importar el documento.
                          </label>
                          <label style={styles.checkboxLabel}>
                            <input type="checkbox" checked={nuevoCampo.uiFilterable}
                              onChange={(e) => setNuevoCampo({ ...nuevoCampo, uiFilterable: e.target.checked })}
                              style={styles.checkbox} />
                            <strong>Filtrable:</strong> Aparece como opción de filtro en búsquedas.
                          </label>
                          <label style={styles.checkboxLabel}>
                            <input type="checkbox" checked={nuevoCampo.uiSortable}
                              onChange={(e) => setNuevoCampo({ ...nuevoCampo, uiSortable: e.target.checked })}
                              style={styles.checkbox} />
                            <strong>Ordenable:</strong> Se puede ordenar la grilla por este campo.
                          </label>
                          <label style={styles.checkboxLabel}>
                            <input type="checkbox" checked={nuevoCampo.uiEditable}
                              onChange={(e) => setNuevoCampo({ ...nuevoCampo, uiEditable: e.target.checked })}
                              style={styles.checkbox} />
                            <strong>Editable:</strong> El revisor puede modificar este valor.
                          </label>
                        </div>
                      </div>
                    </div>
                    <div style={styles.formRow}>
                      <div style={{ ...styles.formField, gridColumn: '1 / -1' }}>
                        <label style={styles.label}>Descripción (opcional)</label>
                        <textarea
                          value={nuevoCampo.description}
                          onChange={(e) => setNuevoCampo({ ...nuevoCampo, description: e.target.value })}
                          style={styles.textarea}
                          placeholder="Descripción del campo..."
                          rows={3}
                        />
                      </div>
                    </div>
                    <div style={styles.formActions}>
                      <button
                        style={styles.saveButton}
                        onClick={agregarCampo}
                      >
                        <AiOutlineSave style={styles.buttonIcon} />
                        Agregar
                      </button>
                      <button
                        style={styles.cancelButton}
                        onClick={cancelarEdicion}
                      >
                        <AiOutlineClose style={styles.buttonIcon} />
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}

                {/* Campos de metadata — nuevo editor plano con strategies (rework #19) */}
                <MetadataFieldsEditor
                  fields={especificacion.fieldSpec}
                  onFieldsChange={(updatedFields) => {
                    const nuevaEspecificacion = { ...especificacion, fieldSpec: updatedFields };
                    setEspecificacion(nuevaEspecificacion);
                    setCambiosNoGuardados(true);
                    // Marcar como modificado
                    const nuevosValoresModificados = new Map(valoresModificados);
                    nuevosValoresModificados.set('fieldSpec', true);
                    setValoresModificados(nuevosValoresModificados);
                    // Pushear al historial para undo/redo
                    const nuevoHistorial = historial.slice(0, indiceHistorial + 1);
                    nuevoHistorial.push({
                      especificacion: JSON.parse(JSON.stringify(nuevaEspecificacion)),
                      valoresModificados: new Map(nuevosValoresModificados),
                      timestamp: Date.now()
                    });
                    setHistorial(nuevoHistorial);
                    setIndiceHistorial(nuevoHistorial.length - 1);
                  }}
                  onDeleteField={(fieldName) => {
                    if (hasDocuments) return;
                    const updatedFields = especificacion.fieldSpec.filter(
                      c => (c.name || c.nombre) !== fieldName
                    );
                    updatedFields.forEach((f, i) => {
                      f.displayOrder = i + 1;
                      f.posicion = i + 1;
                    });
                    const nuevaEspecificacion = { ...especificacion, fieldSpec: updatedFields };
                    setEspecificacion(nuevaEspecificacion);
                    setCambiosNoGuardados(true);
                    // Pushear al historial
                    const nuevosValoresModificados = new Map(valoresModificados);
                    nuevosValoresModificados.set('fieldSpec-delete-' + fieldName, true);
                    setValoresModificados(nuevosValoresModificados);
                    const nuevoHistorial = historial.slice(0, indiceHistorial + 1);
                    nuevoHistorial.push({
                      especificacion: JSON.parse(JSON.stringify(nuevaEspecificacion)),
                      valoresModificados: new Map(nuevosValoresModificados),
                      timestamp: Date.now()
                    });
                    setHistorial(nuevoHistorial);
                    setIndiceHistorial(nuevoHistorial.length - 1);
                  }}
                  hasDocuments={hasDocuments}
                />

                {/* Tabla vieja agrupada por origin — REEMPLAZADA por MetadataFieldsEditor arriba */}
                {false && (() => {
                  const camposOrdenados = especificacion.fieldSpec.sort((a, b) => a.posicion - b.posicion);
                  const camposAgrupados = agruparCamposPorOrigin(camposOrdenados);
                  const origenesOrdenados = ordenarOrigenes(Object.keys(camposAgrupados));

                  return origenesOrdenados.map((origin) => {
                    const camposDelOrigin = camposAgrupados[origin].sort((a, b) => a.posicion - b.posicion);

                    return (
                      <div key={origin} style={styles.originGroup}>
                        <div style={styles.originHeader}>
                          {formatOriginName(origin)}
                        </div>
                        <table style={styles.camposTable}>
                          <thead>
                            <tr>
                              <th style={styles.tableHeader}>Posición</th>
                              <th style={styles.tableHeader}>Nombre</th>
                              <th style={styles.tableHeader}>Etiqueta</th>
                              <th style={styles.tableHeader}>Tipo</th>
                              <th style={styles.tableHeader}>Máscara</th>
                              <th style={styles.tableHeader}>Requerido</th>
                              <th style={styles.tableHeader}>Filtrable</th>
                              <th style={styles.tableHeader}>Ordenable</th>
                              <th style={styles.tableHeader}>Editable</th>
                              <th style={styles.tableHeader}>Ordenar</th>
                              <th style={styles.tableHeader}>Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {camposDelOrigin.map(campo => (
                              <React.Fragment key={campo.nombre}>
                                {campoEditando?.nombre === campo.nombre ? (
                                  <>
                                    {/* Fila de edición */}
                                    <tr style={styles.editRow}>
                                      <td style={styles.tableCell}>{campo.posicion}</td>
                                      <td style={styles.tableCell}>
                                        <div style={styles.tableFieldContainer}>
                                          <input
                                            type="text"
                                            value={nuevoCampo.nombre}
                                            onChange={(e) => setNuevoCampo({ ...nuevoCampo, nombre: e.target.value })}
                                            style={{
                                              ...styles.tableInput,
                                              ...(hasDocuments ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed' } : {})
                                            }}
                                            disabled={hasDocuments}
                                            title={hasDocuments ? "No se puede cambiar el nombre si existen documentos" : ""}
                                          />
                                          <span style={styles.tableFieldHint}>
                                            ID: letras, números, "_" o "-" (sin espacios)
                                          </span>
                                        </div>
                                      </td>
                                      <td style={styles.tableCell}>
                                        <input
                                          type="text"
                                          value={nuevoCampo.label}
                                          onChange={(e) => setNuevoCampo({ ...nuevoCampo, label: e.target.value })}
                                          style={styles.tableInput}
                                        />
                                      </td>
                                      <td style={styles.tableCell}>
                                        <select
                                          value={nuevoCampo.tipo}
                                          onChange={(e) => setNuevoCampo({ ...nuevoCampo, tipo: e.target.value })}
                                          style={{
                                            ...styles.tableSelect,
                                            ...(hasDocuments ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed' } : {})
                                          }}
                                          disabled={hasDocuments}
                                          title={hasDocuments ? "No se puede cambiar el tipo si existen documentos" : ""}
                                        >
                                          <option value="STRING">Texto</option>
                                          <option value="INTEGER">Entero</option>
                                          <option value="LONG">Entero Largo</option>
                                          <option value="DATE">Fecha</option>
                                        </select>
                                      </td>
                                      <td style={styles.tableCell}>
                                        <MaskEditor
                                          value={nuevoCampo.mask}
                                          onChange={(val) => setNuevoCampo({ ...nuevoCampo, mask: val })}
                                          disabled={nuevoCampo.tipo !== 'DATE'}
                                          compact
                                        />
                                      </td>
                                      <td style={styles.tableCell}>
                                        <input
                                          type="checkbox"
                                          checked={nuevoCampo.required}
                                          onChange={(e) => setNuevoCampo({ ...nuevoCampo, required: e.target.checked })}
                                          style={styles.checkbox}
                                        />
                                      </td>
                                      <td style={styles.tableCell}>
                                        <input
                                          type="checkbox"
                                          checked={nuevoCampo.uiFilterable}
                                          onChange={(e) => setNuevoCampo({ ...nuevoCampo, uiFilterable: e.target.checked })}
                                          style={styles.checkbox}
                                        />
                                      </td>
                                      <td style={styles.tableCell}>
                                        <input
                                          type="checkbox"
                                          checked={nuevoCampo.uiSortable}
                                          onChange={(e) => setNuevoCampo({ ...nuevoCampo, uiSortable: e.target.checked })}
                                          style={styles.checkbox}
                                        />
                                      </td>
                                      <td style={styles.tableCell}>
                                        <input
                                          type="checkbox"
                                          checked={nuevoCampo.uiEditable}
                                          onChange={(e) => setNuevoCampo({ ...nuevoCampo, uiEditable: e.target.checked })}
                                          style={styles.checkbox}
                                        />
                                      </td>
                                      <td style={styles.tableCell}>
                                        <div style={styles.ordenarActions}>
                                          <button
                                            style={styles.arrowButton}
                                            onClick={() => moverCampo(campo.nombre, 'up')}
                                            title="Subir posición"
                                            disabled={campo.posicion === 1}
                                          >
                                            ↑
                                          </button>
                                          <button
                                            style={styles.arrowButton}
                                            onClick={() => moverCampo(campo.nombre, 'down')}
                                            title="Bajar posición"
                                            disabled={campo.posicion === especificacion.fieldSpec.length}
                                          >
                                            ↓
                                          </button>
                                        </div>
                                      </td>
                                      <td style={styles.tableCell}>
                                        <div style={styles.campoActions}>
                                          <button
                                            style={styles.saveButton}
                                            onClick={guardarCampo}
                                            title="Guardar cambios"
                                          >
                                            <AiOutlineSave style={styles.buttonIcon} />
                                          </button>
                                          <button
                                            style={styles.cancelButton}
                                            onClick={cancelarEdicion}
                                            title="Cancelar"
                                          >
                                            <AiOutlineClose style={styles.buttonIcon} />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                    {/* Fila adicional para descripción */}
                                    <tr style={styles.editRow}>
                                      <td colSpan="11" style={styles.tableCell}>
                                        <div style={styles.descriptionEditContainer}>
                                          <label style={styles.label}>Descripción (opcional):</label>
                                          <textarea
                                            value={nuevoCampo.description}
                                            onChange={(e) => setNuevoCampo({ ...nuevoCampo, description: e.target.value })}
                                            style={styles.tableTextarea}
                                            placeholder="Descripción del campo..."
                                            rows={2}
                                          />
                                        </div>
                                      </td>
                                    </tr>
                                  </>
                                ) : (
                                  // Fila normal
                                  <tr style={styles.campoRow}>
                                    <td style={valoresModificados.has(`${campo.nombre}-posicion`) ? styles.tableCellModified : styles.tableCell}>{campo.posicion}</td>
                                    <td style={valoresModificados.has(`${campo.nombre}-nombre`) ? styles.tableCellModified : styles.tableCell}>{campo.nombre}</td>
                                    <td style={valoresModificados.has(`${campo.nombre}-label`) ? styles.tableCellModified : styles.tableCell}>{campo.label}</td>
                                    <td style={valoresModificados.has(`${campo.nombre}-tipo`) ? styles.tableCellModified : styles.tableCell}>{getTipoDisplay(campo.tipo)}</td>
                                    <td style={valoresModificados.has(`${campo.nombre}-mask`) ? styles.tableCellModified : styles.tableCell}>
                                      {campo.mask
                                        ? campo.mask.split('|').map(m => m.trim()).filter(Boolean).map((m, i) => (
                                            <span key={i} style={{ display: 'inline-block', background: '#e8f0fe', color: '#1a56db', borderRadius: 4, padding: '1px 6px', marginRight: 3, fontSize: '11px', fontFamily: 'monospace' }}>{m}</span>
                                          ))
                                        : '-'}
                                    </td>
                                    <td style={valoresModificados.has(`${campo.nombre}-required`) ? styles.tableCellModified : styles.tableCell}>
                                      {campo.required ? 'Sí' : 'No'}
                                    </td>
                                    <td style={valoresModificados.has(`${campo.nombre}-uiFilterable`) ? styles.tableCellModified : styles.tableCell}>
                                      {campo.uiFilterable ? 'Sí' : 'No'}
                                    </td>
                                    <td style={valoresModificados.has(`${campo.nombre}-uiSortable`) ? styles.tableCellModified : styles.tableCell}>
                                      {campo.uiSortable ? 'Sí' : 'No'}
                                    </td>
                                    <td style={valoresModificados.has(`${campo.nombre}-uiEditable`) ? styles.tableCellModified : styles.tableCell}>
                                      {campo.uiEditable ? 'Sí' : 'No'}
                                    </td>
                                    <td style={styles.tableCell}>
                                      <div style={styles.ordenarActions}>
                                        <button
                                          style={styles.arrowButton}
                                          onClick={() => moverCampo(campo.nombre, 'up')}
                                          title="Subir posición"
                                          disabled={campo.posicion === 1}
                                        >
                                          ↑
                                        </button>
                                        <button
                                          style={styles.arrowButton}
                                          onClick={() => moverCampo(campo.nombre, 'down')}
                                          title="Bajar posición"
                                          disabled={campo.posicion === especificacion.fieldSpec.length}
                                        >
                                          ↓
                                        </button>
                                      </div>
                                    </td>
                                    <td style={styles.tableCell}>
                                      <div style={styles.campoActions}>
                                        <button
                                          style={styles.editButton}
                                          onClick={() => {
                                            setCampoEditando(campo);
                                            setNuevoCampo({
                                              nombre: campo.nombre,
                                              label: campo.label,
                                              tipo: campo.tipo,
                                              mask: campo.mask || '',
                                              required: campo.required || false,
                                              origin: campo.origin || '',
                                              uiFilterable: campo.uiFilterable || false,
                                              uiSortable: campo.uiSortable || false,
                                              uiEditable: campo.uiEditable || false,
                                              description: campo.description || ''
                                            });
                                            setEditando(true);
                                          }}
                                          title="Editar campo"
                                        >
                                          <AiOutlineEdit style={styles.buttonIcon} />
                                        </button>
                                        <button
                                          style={hasDocuments ? { ...styles.deleteButton, backgroundColor: '#9CA3AF', cursor: 'not-allowed' } : styles.deleteButton}
                                          onClick={hasDocuments ? undefined : () => eliminarCampo(campo.nombre)}
                                          title={hasDocuments ? "No se puede eliminar campos si existen documentos" : "Eliminar campo"}
                                          disabled={hasDocuments}
                                        >
                                          <AiOutlineDelete style={styles.buttonIcon} />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  });
                })()}
              </>
            ) : mode === 'create' ? (
              <div style={styles.loading}>
                Guarde las propiedades de la serie para habilitar la configuración de metadatos.
              </div>
            ) : (
              <div style={styles.loading}>
                Seleccione una serie de la lista para ver y gestionar sus metadatos.
              </div>
            )}
          </div>
        )}
      </div>

      <ImportSerieModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        folders={arbolNodes}
        onImported={(result) => {
          notify.success(`Serie "${result.name}" importada`);
          cargarTiposDocumentos();
        }}
      />

      <FolderCreateModal
        open={folderCreateOpen}
        folders={arbolNodes}
        defaultParent={folderCreateParent}
        onCancel={() => setFolderCreateOpen(false)}
        onSubmit={submitFolderCreate}
      />

      <FolderEditModal
        open={folderEditOpen}
        folder={folderEditing}
        folders={arbolNodes}
        onCancel={() => setFolderEditOpen(false)}
        onSubmit={submitFolderEdit}
      />
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    backgroundColor: '#F8F9FA',
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
  },
  titleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  titleIcon: {
    fontSize: '28px',
    color: '#00468B',
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#00468B',
  },
  content: {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr',
    gap: '30px',
  },
  section: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  sectionTitle: {
    margin: '0 0 20px 0',
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#00468B',
  },
  loading: {
    textAlign: 'center',
    color: '#6B7280',
    padding: '20px',
  },
  errorMessage: {
    backgroundColor: '#FEF2F2',
    border: '1px solid #FECACA',
    color: '#DC2626',
    padding: '12px 16px',
    borderRadius: '6px',
    marginBottom: '20px',
  },
  tiposTable: {
    width: '100%',
    borderCollapse: 'collapse',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: '#FFFFFF',
  },
  treeWrapper: {
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    overflow: 'hidden',
    height: '70vh',
    minHeight: '400px',
    display: 'flex',
  },
  tableHeader: {
    backgroundColor: '#F9FAFB',
    padding: '12px 16px',
    textAlign: 'left',
    fontWeight: '600',
    fontSize: '14px',
    color: '#374151',
    borderBottom: '1px solid #E5E7EB',
  },
  tableRow: {
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    borderBottom: '1px solid #F3F4F6',
  },
  tableRowSelected: {
    backgroundColor: '#F0F9FF',
  },
  tableCell: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#374151',
    borderBottom: '1px solid #F3F4F6',
  },
  tableCellModified: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#374151',
    borderBottom: '1px solid #F3F4F6',
    backgroundColor: '#FEF3C7',
    borderLeft: '3px solid #F59E0B',
  },
  originGroup: {
    marginBottom: '30px',
  },
  originHeader: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#111827',
    marginBottom: '12px',
    paddingBottom: '8px',
    borderBottom: '2px solid #E5E7EB',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  camposTable: {
    width: '100%',
    borderCollapse: 'collapse',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: '#FFFFFF',
    marginTop: '0',
  },
  campoRow: {
    transition: 'background-color 0.2s ease',
    borderBottom: '1px solid #F3F4F6',
  },
  editRow: {
    backgroundColor: '#F0F9FF',
    borderBottom: '1px solid #F3F4F6',
  },
  tableInput: {
    width: '100%',
    padding: '6px 8px',
    border: '1px solid #D1D5DB',
    borderRadius: '4px',
    fontSize: '14px',
    backgroundColor: '#FFFFFF',
  },
  tableSelect: {
    width: '100%',
    padding: '6px 8px',
    border: '1px solid #D1D5DB',
    borderRadius: '4px',
    fontSize: '14px',
    backgroundColor: '#FFFFFF',
  },
  specHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  undoRedoContainer: {
    display: 'flex',
    gap: '4px',
  },
  undoRedoButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    backgroundColor: '#FFFFFF',
    color: '#374151',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
    transition: 'all 0.2s ease',
  },
  undoRedoButtonDisabled: {
    backgroundColor: '#F9FAFB',
    color: '#9CA3AF',
    cursor: 'not-allowed',
  },
  resetButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    border: '1px solid #DC2626',
    borderRadius: '6px',
    backgroundColor: '#FEF2F2',
    color: '#DC2626',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
    transition: 'all 0.2s ease',
    marginLeft: '8px',
  },
  saveChangesButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: '#10B981',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  saveChangesButtonDisabled: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: '#9CA3AF',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'not-allowed',
    transition: 'all 0.2s ease',
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: '#00468B',
    color: '#FFFFFF',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
  },
  formContainer: {
    backgroundColor: '#F8F9FA',
    border: '1px solid #E5E7EB',
    borderRadius: '6px',
    padding: '20px',
    marginBottom: '20px',
  },
  formTitle: {
    margin: '0 0 16px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#374151',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '16px',
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    cursor: 'pointer',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
  },
  label: {
    fontSize: '12px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '4px',
  },
  fieldHint: {
    marginTop: '4px',
    fontSize: '11px',
    color: '#6B7280',
    lineHeight: 1.3,
  },
  tableFieldContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  tableFieldHint: {
    fontSize: '10px',
    color: '#6B7280',
    lineHeight: 1.2,
  },
  input: {
    padding: '8px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '4px',
    fontSize: '14px',
  },
  textarea: {
    padding: '8px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '4px',
    fontSize: '14px',
    resize: 'vertical',
  },
  descriptionEditContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '8px 0',
  },
  tableTextarea: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '4px',
    fontSize: '14px',
    resize: 'vertical',
  },
  select: {
    padding: '8px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '4px',
    fontSize: '14px',
    backgroundColor: '#FFFFFF',
  },
  formActions: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
  },
  saveButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: '#10B981',
    color: '#FFFFFF',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  cancelButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: '#6B7280',
    color: '#FFFFFF',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  camposList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  campoItem: {
    border: '1px solid #E5E7EB',
    borderRadius: '6px',
    overflow: 'hidden',
  },
  campoContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
  },
  campoInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
  },
  campoPosicion: {
    backgroundColor: '#00468B',
    color: '#FFFFFF',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 'bold',
    minWidth: '30px',
    textAlign: 'center',
  },
  campoDetalles: {
    flex: 1,
  },
  campoNombre: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
  },
  campoLabel: {
    fontSize: '13px',
    color: '#6B7280',
    marginBottom: '2px',
  },
  campoTipo: {
    fontSize: '12px',
    color: '#9CA3AF',
  },
  campoActions: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'center',
  },
  ordenarActions: {
    display: 'flex',
    flexDirection: 'row',
    gap: '2px',
    alignItems: 'center',
    justifyContent: 'center',
  },
  arrowButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '18px',
    height: '18px',
    border: '1px solid #D1D5DB',
    borderRadius: '3px',
    backgroundColor: '#FFFFFF',
    color: '#374151',
    cursor: 'pointer',
    fontSize: '10px',
    fontWeight: 'bold',
    transition: 'all 0.2s ease',
    padding: '0',
    margin: '0',
  },
  editButton: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#F3F4F6',
    color: '#374151',
    border: '1px solid #D1D5DB',
    padding: '6px',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  deleteButton: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#FEF2F2',
    color: '#DC2626',
    border: '1px solid #FECACA',
    padding: '6px',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  editForm: {
    padding: '16px',
    backgroundColor: '#F8F9FA',
  },
  buttonIcon: {
    fontSize: '14px',
  },
  backButton: {
    backgroundColor: '#6B7280',
    color: '#FFFFFF',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  backIcon: {
    fontSize: '16px',
  },
};

export default DocumentSeriesAdmin;
